import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import { compose } from 'ramda';
import { RouteComponentProps } from 'react-router-dom';
import queryString from 'query-string';
import Grid from '@material-ui/core/Grid';
import { showToast, Button, Search, Select } from '@deeptrue-frontend/ui';

import ProjectTopNavbar from 'components/Projects/ProjectTopNavbar';
import { ReactComponent as ArrowIcon } from 'assets/icons/arrow.svg';
import { OrganizationType } from 'store/modules/auth/auth';
import { Dashboard } from 'components/Layout';
import { withMetaDescription } from 'components/HeadProvider/HeadProvider';
import { ProjectsReducerType } from 'store/modules/projects/projects.types';
import QUERIES from 'consts/queries';
import ProjectCard from './ProjectCard';
import api from 'api/api';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import { useFormatMessage } from 'components/IntlProvider';
import AnimatedPlaceholder from 'components/AnimatedPlaceholder/AnimatedPlaceholder';

const SelectorsWrapper = styled.div`
  display: flex;
  align-items: center;

  label {
    display: flex;
    align-items: center;
    margin-bottom: 0;
    margin-right: 0.5rem;

    p {
      margin: 0;
      white-space: nowrap;
      margin-right: 0.5rem;
    }
  }
`;

const StatusWrapper = styled(SelectorsWrapper)`
  label {
    margin-right: 0;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 50px;
  width: 50px;
  height: 50px;
  background-color: #ffffff;
  border: 1px solid #d6d6d6;
`;

const SortIcon = styled(ArrowIcon)<{ asc: string }>`
  cursor: pointer;
  transition: all 0.3s;
  transform: ${({ asc }) => (JSON.parse(asc) ? 'initial' : 'rotate(180deg)')};
`;

const SearchStyled = styled(Search)`
  background-color: #ffffff;
`;

const ProjectNavbar = styled.nav`
  display: grid;
  grid-template-columns: 1fr;
  column-gap: 1rem;
  width: 100%;

  .project__filters-dropdown {
    display: grid;
    column-gap: 1rem;
    grid-template-columns: 1fr 1fr 50px;

    @media screen and (min-width: 1200px) {
      grid-template-columns: 280px 280px 50px;
    }
  }
  @media screen and (min-width: 1200px) {
    grid-template-columns: 1fr auto;
  }
`;

const SelectSortStyled = styled.div`
  label {
    display: grid;
    grid-template-columns: auto 1fr;
    column-gap: 1rem;
    align-items: baseline;
    p {
      flex: none;
    }
  }
`;

const { connect, actions, selectors } = StoreProvider;

let timer: number | undefined | ReturnType<typeof setTimeout>;

interface ExtendedProjectsProps {
  projects: ProjectsReducerType['list'];
  setProjects: (payload: ProjectsReducerType['list']) => void;
  clearProjects: () => void;
  organization: OrganizationType;
  language: string;
  isBexioUser: boolean;
  isGuestUser: boolean;
}

type ProjectsParams = {
  [P in 'limit' | 'offset' | 'search' | 'status' | 'ordering']?: string;
};

enum sortOptions {
  created_at = 'Created at',
  updated_at = 'Updated at',
  status = 'Status',
  product_slug = 'Product slug',
  name = 'Name',
}

const Projects = ({
  projects,
  setProjects,
  organization,
  location,
  clearProjects,
  language = 'en',
  isBexioUser,
  history,
  isGuestUser,
}: ExtendedProjectsProps & RouteComponentProps) => {
  const t = useFormatMessage();
  const defaultOrderOption = `-${Object.keys(sortOptions)[0]}`;

  const [isLoaded, setIsLoaded] = useState(false);
  const [params, setParams] = useState<ProjectsParams>({
    ordering: defaultOrderOption,
    limit: '20',
  });
  const { search } = location;
  const isDisplayAllTab = search.length < 1;

  const refreshProjectList = () => {
    setIsLoaded(false);
    return api.resources
      .getProjects({
        organizationCodename: organization.codename,
        params,
      })
      .then(({ data }) => {
        if (params?.offset) {
          setProjects({
            ...data,
            results: [...projects.results, ...data.results],
          });
        } else {
          setProjects(data);
        }
      })
      .catch(() => {
        showToast({
          type: 'error',
          title: t(
            'errors.toast.general',
            'Something went wrong, please contact administrator'
          ),
        });
      })
      .finally(() => {
        setIsLoaded(true);
      });
  };

  useEffect(() => {
    refreshProjectList();
    // eslint-disable-next-line
  }, [params]);

  useEffect(() => {
    const query = queryString.parse(location.search);
    if (query.action === QUERIES.PROJECT_DELETED) {
      showToast({
        type: 'success',
        title: t('errors.project.deleted', 'Project was succesfully deleted'),
      });
    }
    if (query.action === QUERIES.PROJECT_ARCHIVED) {
      showToast({
        type: 'success',
        title: t('errors.project.archived', 'Project was succesfully archived'),
      });
    }
    if (query.action === QUERIES.PROJECT_RESTORED) {
      showToast({
        type: 'success',
        title: t('errors.project.restored', 'Project was succesfully restored'),
      });
    }

    setParams({
      ...params,
      status:
        query.status && query.status !== '0'
          ? (query.status as string)
          : undefined,
    });
    // eslint-disable-next-line
  }, [location.search]);

  useEffect(() => {
    return () => {
      clearProjects();
    };
    // eslint-disable-next-line
  }, []);

  const isAscOrder = () => !params.ordering?.includes('-');

  const getSortOptions = () =>
    Object.keys(sortOptions).map((option) => ({
      value: option,
      // @ts-ignore
      label: t(`projects.sortby.${option}`, sortOptions[option]),
    }));

  const onLoadMoreProjects = (nextUrl = '') => {
    const nextParsedUrl = new URL(nextUrl);
    const urlParams = queryString.parse(nextParsedUrl.search);

    setParams({ ...params, offset: urlParams.offset as string });
  };

  const onSearch = (e: React.ChangeEvent<{ value: string }>) => {
    const { value } = e.target;
    clearTimeout(timer as number);

    timer = setTimeout(() => {
      setParams({ ...params, search: value });
    }, 300);
  };

  const onSortOptionChange = (name: string, value: string | string[]) => {
    if (name === 'status') {
      const isAnySelected = value.length > 0;
      if (!isAnySelected) {
        return setParams({ ...params, [name]: '99' });
      }
      if (value.includes('all')) {
        return setParams({ ...params, [name]: undefined });
      }
      return setParams({ ...params, [name]: value.toString() });
    }
    setParams({ ...params, [name]: value });
  };

  const onSortOrderChange = () => {
    const orderingPrefix = isAscOrder() ? '-' : '';
    setParams({
      ...params,
      ordering: orderingPrefix + params.ordering?.replace('-', ''),
    });
  };

  const statusOptions = [
    {
      value: 'all',
      label: t('projects.filter.status.all', 'All'),
      exception: true,
    },
    { value: 1, label: t('projects.filter.status.draft', 'Draft') },
    { value: 2, label: t('projects.filter.status.accepted', 'Accepted') },
    { value: 11, label: t('projects.filter.status.finished', 'Finished') },
    { value: 12, label: t('projects.filter.status.archived', 'Archived') },
    { value: 13, label: t('projects.filter.status.runing', 'In progress') },
  ];

  useEffect(() => {
    if (isLoaded) {
      if (isBexioUser) {
        const isBexioProjectAlreadyCreated = projects.results.length > 0;
        if (isBexioProjectAlreadyCreated) {
          const bexioProject = projects.results[0];
          // redirect to project
          history.push(`/project/${bexioProject.id}/review`);
        } else {
          // TODO add brief URL - CHECK IF THAT IS PROPER ONE
          // redirect to briefeing
          history.push(
            `/organization/${organization.codename}/products/bexio-satisfaction`
          );
        }
      }
    }
    // eslint-disable-next-line
  }, [isLoaded]);

  if (isBexioUser) return null;

  return (
    <Dashboard
      title={t('projects.overview', 'Projects overview')}
      location={location}
      headerChildren={!isBexioUser && !isGuestUser && <ProjectTopNavbar />}
    >
      <AnimatedPlaceholder
        isLoaded={isLoaded}
        hasValues={projects.count > 0}
        text={t('noresults', 'No results. We have nothing to show here.')}
      >
        <>
          <Grid container>
            <Grid
              container
              justify="space-between"
              style={{ margin: '3rem 1rem 2rem' }}
              className="project__filters"
            >
              <ProjectNavbar>
                <div>
                  <SearchStyled
                    placeholder={t(
                      'projects.search.placeholder',
                      'Search projects'
                    )}
                    onChange={onSearch}
                  />
                </div>
                <div className="project__filters-dropdown">
                  <div>
                    {isDisplayAllTab && (
                      <StatusWrapper>
                        <Select
                          name="status"
                          value="all"
                          label={t('projects.status.label', 'Status')}
                          options={statusOptions}
                          multiselect
                          onChange={onSortOptionChange}
                        />
                      </StatusWrapper>
                    )}
                  </div>
                  <div>
                    <SelectSortStyled>
                      <Select
                        name="ordering"
                        value={params.ordering?.replace('-', '')}
                        label={t('projects.sortby.label', 'Sort by')}
                        options={getSortOptions()}
                        onChange={onSortOptionChange}
                      />
                    </SelectSortStyled>
                  </div>
                  <div>
                    <IconWrapper>
                      <SortIcon
                        onClick={onSortOrderChange}
                        asc={String(isAscOrder())}
                        title={t('projects.sortby.sortOrder', 'Sort order')}
                      />
                    </IconWrapper>
                  </div>
                </div>
              </ProjectNavbar>
            </Grid>
          </Grid>
          {projects.results.map((project: ProjectsReducerType['selected']) => {
            return (
              <ProjectCard
                key={project.id}
                progress={project.progress}
                status={project.status.value}
                id={project.id}
                name={project.name}
                brand={project.brand}
                createdAt={project.created_at}
                updatedAt={project.updated_at}
                link={`/project/${project.id}/review`}
                lang={language}
                productName={project.product_name}
              />
            );
          })}
        </>
      </AnimatedPlaceholder>
      {projects.next !== null && (
        <Grid container justify="center">
          <Button
            variant="primary"
            onClick={() => onLoadMoreProjects(projects.next || '')}
          >
            {t('projects.list.more', 'More')}
          </Button>
        </Grid>
      )}
    </Dashboard>
  );
};

export default compose(
  connect(
    (state) => ({
      language: selectors.intlSelectors.language(state),
      projects: selectors.projects.list(state),
      organization: selectors.authSelectors.userOrganization(state),
      isBexioUser: selectors.authSelectors.isBexioUser(state),
      isGuestUser: selectors.authSelectors.isOrganizationGuest(state),
    }),
    {
      setProjects: actions.projects.setProjects,
      clearProjects: actions.projects.clearProjects,
    }
  ),
  withMetaDescription({ title: 'Dashboard projects' })
)(Projects);
